import { useEffect, useMemo, useState } from "react";
import CustomButton from "components/CustomButton/CustomButton";
import ArrowIcon from "icons/ArrowIcon";
import UpdateIcon from "icons/UpdateIcon";
import {
	AccordionTrigger,
	BatchId,
	BatchIdentity,
	BatchRow,
	BatchMetaLine,
	BatchMetaPill,
	BatchMetaPillGroup,
	BatchTableCell,
	BatchTableHeader,
	BatchType,
	CellLabel,
	DashboardCard,
	EmptyState,
	PrimaryValue,
	ResultPill,
	ResultStack,
	SecondaryValue,
	SectionHeaderRow,
	SectionTitle,
	SectionTitleGroup,
	StatusPill,
	TableShell,
} from "../styles";

const BatchAuditSection = ({
	batches,
	isLoading,
	isBusy,
	hasLoaded,
	onLoadBatches,
	formatDate,
	getStatusText,
	getStatusVariant,
	getBatchTypeText,
}) => {
	const [expanded, setExpanded] = useState(false);

	const getBatchRowTone = batch =>
		batch.status === "COMPLETED_WITH_ERRORS" || (batch.failedCount || 0) > 0
			? "critical"
			: "default";

	useEffect(() => {
		if (expanded && !hasLoaded && !isLoading) {
			onLoadBatches?.();
		}
	}, [expanded, hasLoaded, isLoading, onLoadBatches]);

	const latestBatch = batches?.[0] || null;

	const summaryText = useMemo(() => {
		if (isLoading && !hasLoaded) return "Cargando historial...";
		if (!hasLoaded) return "Cargar historial";
		if (!batches.length) return "No hay lotes registrados.";
		return `${batches.length} lote(s)`;
	}, [batches.length, hasLoaded, isLoading]);

	return (
		<DashboardCard gap="16px">
			<SectionHeaderRow>
				<SectionTitleGroup>
					<AccordionTrigger
						type="button"
						$expanded={expanded}
						disabled={isBusy || isLoading}
						onClick={() => setExpanded(current => !current)}
						aria-expanded={expanded}
						aria-label={
							expanded
								? "Ocultar historial de lotes"
								: "Ver historial de lotes"
						}
					>
						<SectionTitle as="span">Historial de lotes</SectionTitle>
						<ArrowIcon color="#5B36F2" />
					</AccordionTrigger>
					<SecondaryValue>{summaryText}</SecondaryValue>
				</SectionTitleGroup>
			</SectionHeaderRow>

			{expanded ? (
				<TableShell>
					{hasLoaded && latestBatch ? (
						<BatchMetaLine>
							<BatchMetaPillGroup>
								<BatchMetaPill>
									Estado: {getStatusText(latestBatch.status)}
								</BatchMetaPill>
								<BatchMetaPill>
									Rev. {latestBatch.configRevision || 1}
								</BatchMetaPill>
								<BatchMetaPill>
									Fecha: {formatDate(latestBatch.requestedAt)}
								</BatchMetaPill>
							</BatchMetaPillGroup>
							{hasLoaded ? (
								<CustomButton
									variant="terciary"
									size="very-small"
									icon={<UpdateIcon size={16} />}
									title="Actualizar historial"
									ariaLabel="Actualizar historial de lotes"
									disabled={isBusy || isLoading}
									onClick={onLoadBatches}
								/>
							) : null}
						</BatchMetaLine>
					) : null}

				<BatchTableHeader>
					<span>Lote</span>
					<span>Estado</span>
					<span>Rev.</span>
					<span>Procesados</span>
					<span>Resultado agregado</span>
					<span>Fecha</span>
				</BatchTableHeader>

				{isLoading ? (
					<EmptyState>Cargando lotes...</EmptyState>
				) : batches.length === 0 ? (
					<EmptyState>No hay lotes registrados.</EmptyState>
				) : (
					batches.map(batch => (
						<BatchRow key={batch.batchId} $tone={getBatchRowTone(batch)}>
							<BatchTableCell>
								<CellLabel>Lote</CellLabel>
								<BatchIdentity>
									<BatchType title={batch.batchType || "-"}>
										{getBatchTypeText(batch.batchType)}
									</BatchType>
									<BatchId>#{batch.batchId}</BatchId>
								</BatchIdentity>
							</BatchTableCell>

							<BatchTableCell>
								<CellLabel>Estado</CellLabel>
								<StatusPill $variant={getStatusVariant(batch.status)}>
									{getStatusText(batch.status)}
								</StatusPill>
							</BatchTableCell>

							<BatchTableCell>
								<CellLabel>Revisión</CellLabel>
								<PrimaryValue>{batch.configRevision || 1}</PrimaryValue>
							</BatchTableCell>

							<BatchTableCell>
								<CellLabel>Procesados</CellLabel>
								<PrimaryValue>
									{batch.processedCount || 0}/{batch.totalStudents || 0}
								</PrimaryValue>
							</BatchTableCell>

							<BatchTableCell>
								<CellLabel>Resultado agregado</CellLabel>
								<ResultStack>
									<ResultPill>{batch.issuedCount || 0} emitidos</ResultPill>
									<ResultPill>{batch.skippedCount || 0} omitidos</ResultPill>
									<ResultPill>{batch.failedCount || 0} errores</ResultPill>
								</ResultStack>
							</BatchTableCell>

							<BatchTableCell>
								<CellLabel>Fecha</CellLabel>
								<PrimaryValue>{formatDate(batch.requestedAt)}</PrimaryValue>
							</BatchTableCell>
						</BatchRow>
					))
				)}
				</TableShell>
			) : null}
		</DashboardCard>
	);
};

export default BatchAuditSection;
